/*
*   @site           Preloaded: Site
*   @function       Layout Controller
*   @author         Last17
*   @copyright      Copyright 2025, Preloaded.com
*   @version        0.01
*
*********************************************************************************************/


//-----------------------------------------------
// Imports
//-----------------------------------------------

//-----------------------------------------------
// Class Definition
//-----------------------------------------------
class _LayoutController {


  //-----------------------------------------------
  // Constructor
  //-----------------------------------------------
  constructor() {
    //Current layout
    this.layout = 'desktop';
    document.body.classList.add(this.layout);

    //Scroll events
    this.scrollFunctions = [];

    //Size
    this.size = { window: { width: 0, height: 0 } };

    //Scroll
    this.scroll = {
      last: { x: 0, y: 0 },
      current: { x: 0, y: 0 },
      direction: { x: 0, y: 0 }, //0 is stopped
      directionChangedAt: { position:{x: -1, y: -1}, direction:{x: 0, y:0} } //0 is stopped
    };

    //Scroll thresholds
    this.thresholds = {
      x:{left: 10, right: 10}, //pull these in from the
      y:{up: 50, down: 20}
    };
  }


  //-----------------------------------------------
  // Init
  //-----------------------------------------------
  init() {
    //Scroll DO
    window.addEventListener('scroll', (event) => {
      this.scrollDo(event);
    });

    //Scroll STOP
    window.onscrollend = event => {
      this.scrollStop(event);
    };

    //Init
    this.scrollSet(Math.max(0, window.scrollX), Math.max(0, window.scrollY), 'init');

    //Layout
    this.setLayout();

    //Resize
    window.onresize = () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }

      //De-bounced
      this.resizeTimeout = setTimeout(() => {
        this.resize();
      }, 25);
    };

  }


  //-----------------------------------------------
  // Listen to scroll
  //-----------------------------------------------
  listenToScroll(params) {
    this.scrollFunctions.push(params);
  }


  //-----------------------------------------------
  // Scroll do
  //-----------------------------------------------
  scrollDo(event) {
    this.scrollSet(Math.max(0, window.scrollX), Math.max(0, window.scrollY), 'move'); //don't do negative values
  }

  //-----------------------------------------------
  // Scroll stop
  //-----------------------------------------------
  scrollStop(event) {
    this.scrollSet(this.scroll.current.x, this.scroll.current.y, 'stop');
  }

  //-----------------------------------------------
  // Scroll set
  //-----------------------------------------------
  scrollSet(x, y, reason) {
    if (x !== this.scroll.last.x || y !== this.scroll.last.y) {
      //Set LAST position
      this.scroll.last.x = this.scroll.current.x;
      this.scroll.last.y = this.scroll.current.y;

      //Calculate direction
      let directionX = (x > this.scroll.current.x) ? 1 : (x < this.scroll.current.x) ? -1 : 0;
      let directionY = (y > this.scroll.current.y) ? 1 : (y < this.scroll.current.y) ? -1 : 0;

      //Have any directions changed?
      let directionChanged = false;
      if ((directionX !== this.scroll.direction.x || directionY !== this.scroll.direction.y) && reason !== 'init') {
        //Store new directions
        if(y === 0 && directionY === -1) {
          //Scrolling up and hit the top of the page
          this.scroll.direction.x = directionX;
          this.scroll.direction.y = directionY;
          directionChanged = true;
        } else if(this.scroll.directionChangedAt.direction.x !== directionX || this.scroll.directionChangedAt.direction.y !== directionY) {
          //Store the data about the change (i.e. where it happened and what the scroll criteria were)
          this.scroll.directionChangedAt.position.x = x;
          this.scroll.directionChangedAt.position.y = y;
          this.scroll.directionChangedAt.direction.x = directionX;
          this.scroll.directionChangedAt.direction.y = directionY;
        } else {
          //Something is stored already that matches the same criteria, so decide if it is significant or not
          let thresholdX = (directionX === 1) ? this.thresholds.y.right : this.thresholds.y.left;
          let thresholdY = (directionY === 1) ? this.thresholds.y.down : this.thresholds.y.up;
          if(Math.abs(y - this.scroll.directionChangedAt.position.y) > thresholdY || Math.abs(x - this.scroll.directionChangedAt.position.x) > thresholdX) {
            this.scroll.direction.x = directionX;
            this.scroll.direction.y = directionY;
            directionChanged = true;
          } 
        }
      }

      //Set the new values
      this.scroll.current.x = x;
      this.scroll.current.y = y;

      //Fire an event for anything listening through pubsub: remove this @TODO (navigation is listening)
      PubSub.publish('scroll', { data: this.scroll, directionChanged: directionChanged });

      //Loop through any funcs and call all of them
      for(let i = 0 ; i < this.scrollFunctions.length; i++) {
        this.scrollFunctions[i].func.apply(this.scrollFunctions[i].scope, [{ data: this.scroll, directionChanged: directionChanged }]);
      }
    }
  }

  //-----------------------------------------------
  // Resize
  //-----------------------------------------------
  resize() {
    this.resizeTimeout = null;
    this.setLayout();
  }

  //-----------------------------------------------
  // Load
  //-----------------------------------------------
  setLayout() {
    //Layout
    let layout = (window.innerWidth <= 767) ? 'mobile' : 'desktop';
    let layoutChanged = false;

    //New layout?
    if (layout !== this.layout) {
      //Add to document body
      document.body.classList.remove(this.layout);
      document.body.classList.add(layout);

      //Flag
      this.layout = layout;
      layoutChanged = true;
    }

    //Window size
    let widthChanged = false;
    let heightChanged = false;
    if (this.size.window.width !== window.innerWidth) {
      this.size.window.width = window.innerWidth;
      widthChanged = true;
    }
    if (this.size.window.height !== window.innerHeight) {
      this.size.window.height = window.innerHeight;
      heightChanged = true;
    }

    //Fire an event
    PubSub.publish('resize', {
      layout: this.layout,
      size: this.size,
      changed: {
        layout: layoutChanged,
        width: widthChanged,
        height: heightChanged
      }
    });
  }

}


//--------------------------------------------------------------------------
//  Export
//--------------------------------------------------------------------------
const LayoutController = new _LayoutController();
export default LayoutController;